import { Model, ModelApiName, ImageModel } from 'api/Exec/types';
import {
  useModelEcrs,
  useAddDockerImage,
  useModelEcrsColumns,
  useImageModelColumns,
} from 'components/Dashboard/Models/DockerImages/DockerImageForm/hooks';
import { selectModelsApi } from 'store/exec/execSlice';
import { useAppSelector } from 'store/hooks';

import React, { useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Table, Typography } from 'antd';

interface FormValues {
  ecrImageId: string;
  modelApiName: ModelApiName;
  description?: string;
}

interface Props {
  onCancel: () => void;
  onDockerImageAdded: (model: Model) => void;
}

const DockerImageForm: React.FC<Props> = ({ onCancel, onDockerImageAdded }) => {
  const [ form ] = Form.useForm<FormValues>();
  const modelsApi = useAppSelector(selectModelsApi);
  const { addDockerImage, isCreating } = useAddDockerImage();
  const { fetchDockerImages, dockerImages, isLoading } = useModelEcrs();

  const selectRow = (id: string) => form.setFieldValue('ecrImageId', id);

  const onFinish = async (values: FormValues) => {
    const dockerImage = await addDockerImage(values.ecrImageId, values.modelApiName, values.description);
    if (dockerImage) {
      onDockerImageAdded(dockerImage);
      form.resetFields();
    }
  };

  const [ imageModels, setImageModels ] = useState<ImageModel[] | null>(null);
  const openImageModelsModal = (models: ImageModel[]) => setImageModels(models);
  const closeImageModelsModal = () => setImageModels(null);

  const { columns } = useModelEcrsColumns(openImageModelsModal);
  const { columns: imageModelColumns } = useImageModelColumns();

  const isProcessing = isLoading || isCreating;

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={() => fetchDockerImages(false)} disabled={isProcessing}>Fetch</Button>
        <Button type="primary" onClick={() => fetchDockerImages(true)} disabled={isProcessing}>Refresh</Button>
      </Space>

      {dockerImages.length > 0 && (
        <Typography.Title level={5}>{`${dockerImages[0].imageRegistry}/${dockerImages[0].imageRepository}`}</Typography.Title>
      )}

      <Table
        rowKey={record => record.id}
        columns={columns}
        dataSource={dockerImages}
        rowSelection={{
          type: 'radio',
          onSelect: ({ id }) => selectRow(id),
        }}
        scroll={{ x: true }}
        loading={isProcessing}
      />

      <Form
        form={form}
        name="dockerImage"
        style={{ width: 360, marginTop: -50 }}
        layout="vertical"
        onFinish={onFinish}
        disabled={isProcessing}
      >
        <Form.Item
          name="ecrImageId"
          label="Selected ECR image id"
          rules={[ { required: true, message: 'Select ECR image from the table' } ]}
        >
          <Input disabled placeholder="Selected ECR image id" />
        </Form.Item>

        <Form.Item
          name="modelApiName"
          label="Model API"
          rules={[ { required: true, message: 'Field is mandatory' } ]}
        >
          <Select placeholder="Select model API" options={modelsApi} loading={isProcessing} />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <Input.TextArea placeholder="Enter desciption" disabled={isProcessing} />
        </Form.Item>

        <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
          <Space>
            <Button onClick={onCancel}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Add docker image
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Modal
        title={<Typography.Title level={4}>Image models</Typography.Title>}
        open={!!imageModels}
        footer={null}
        width={640}
        onCancel={closeImageModelsModal}
      >
        {imageModels && (
          <Table
            rowKey={record => record.id}
            columns={imageModelColumns}
            dataSource={imageModels}
            pagination={false}
          />
        )}
      </Modal>
    </>
  );
};

export default DockerImageForm;
